import { translateLabel } from "@/i18n/messages";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StarIcon, XIcon } from "@phosphor-icons/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import {
  communityRatingText,
  DESTINATION_REVIEW_TAGS,
  REVIEW_TAG_LABELS,
  reviewFormFromExisting,
  reviewInputFromForm,
  toggleReviewTag,
} from "@/lib/destinationReviews";
import { fetchDestinationCommunityReviews, setReviewHelpful } from "@/lib/reviewCommunity";
import { ReportContent } from "@/components/ReportContent";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

function Stars({ value, interactive = false, onChange, testIdPrefix = "review-star" }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${value || 0} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const icon = <StarIcon size={interactive ? 28 : 17} weight={star <= value ? "fill" : "regular"} />;
        if (!interactive) return <span key={star} className={star <= value ? "text-[#CCFF00]" : "text-zinc-600"}>{icon}</span>;
        return (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            data-testid={`${testIdPrefix}-${star}`}
            aria-label={`${star} star${star === 1 ? "" : "s"}`}
            className={`transition-transform hover:scale-110 ${star <= value ? "text-[#CCFF00]" : "text-zinc-600 hover:text-zinc-400"}`}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}

export function DestinationReviews({ destinationId, compact = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, requestDestinationReview } = useAuth();
  const [data, setData] = useState({ summary: { average: null, count: 0 }, reviews: [] });
  const [ownReview, setOwnReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(reviewFormFromExisting(null));
  const [saving, setSaving] = useState(false);
  const [helpfulSavingId, setHelpfulSavingId] = useState(null);

  const wantsEditor = searchParams.get("review") === "write";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const publicRequest = fetchDestinationCommunityReviews(destinationId);
      const ownRequest = user ? api.get(`/parks/${destinationId}/reviews/mine`) : Promise.resolve({ data: null });
      const [publicResult, ownResult] = await Promise.all([publicRequest, ownRequest]);
      setData(publicResult);
      setOwnReview(ownResult.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [destinationId, user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!loading && user && wantsEditor) {
      setForm(reviewFormFromExisting(ownReview));
      setEditing(true);
    }
  }, [loading, ownReview, user, wantsEditor]);

  const beginReview = async () => {
    if (!user) {
      requestDestinationReview(destinationId);
      toast(translateLabel("Sign in to write a review"), { description: translateLabel("We’ll bring you back to the review editor.") });
      navigate("/login");
      return;
    }
    setForm(reviewFormFromExisting(ownReview));
    setEditing(true);
  };

  const closeEditor = () => {
    setEditing(false);
    if (wantsEditor) {
      const next = new URLSearchParams(searchParams);
      next.delete("review");
      setSearchParams(next, { replace: true });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.rating < 1 || form.rating > 5) {
      toast.error(translateLabel("Choose a rating from 1 to 5 stars"));
      return;
    }
    setSaving(true);
    try {
      await api.post(`/parks/${destinationId}/reviews`, reviewInputFromForm(form));
      toast.success(ownReview ? "Review updated" : "Review published");
      closeEditor();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!ownReview) return;
    setSaving(true);
    try {
      await api.delete(`/parks/${destinationId}/reviews`);
      toast.success(translateLabel("Review removed"));
      closeEditor();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleHelpful = async (review) => {
    if (!user) {
      toast("Sign in to mark reviews as helpful");
      navigate("/login");
      return;
    }

    const next = !review.viewer_has_helpful;
    setHelpfulSavingId(review.id);
    try {
      const result = await setReviewHelpful(review.id, next);
      setData((current) => ({
        ...current,
        reviews: (current.reviews || []).map((item) => item.id === review.id
          ? { ...item, helpful_count: result.helpful_count, viewer_has_helpful: result.helpful }
          : item),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail || err.message));
    } finally {
      setHelpfulSavingId(null);
    }
  };

  const reviews = useMemo(() => data.reviews ?? [], [data.reviews]);

  return (
    <section id="reviews" data-detail-section="reviews" className={`${compact ? "mb-8" : "mb-12"} scroll-mt-24`} data-testid="destination-reviews">
      <div className={`${compact ? "mb-3 items-center" : "mb-4 items-end"} flex flex-wrap justify-between gap-3`}>
        <div>
          <h2 className={`font-display font-bold uppercase text-white ${compact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"}`}>{translateLabel("Community Reviews")}</h2>
          <p className={`mt-1 text-zinc-400 ${compact ? "text-xs" : "text-sm"}`}>{translateLabel("Ratings and reviews from riders.")}</p>
        </div>
        <button
          type="button"
          onClick={beginReview}
          data-testid="write-review-btn"
          className={`${compact ? "w-auto px-4 py-2 text-xs" : "w-full px-5 py-2.5 text-sm sm:w-auto"} rounded-full bg-[#CCFF00] font-bold text-black transition-transform active:scale-95`}
        >
          {ownReview ? "Edit your review" : "Write a review"}
        </button>
      </div>

      {loading ? (
        compact ? (
          <Skeleton className="h-20 w-full rounded-2xl bg-white/5" />
        ) : (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full rounded-2xl bg-white/5" />
            <Skeleton className="h-36 w-full rounded-2xl bg-white/5" />
          </div>
        )
      ) : error ? (
        <div className={`rounded-2xl border border-red-500/20 bg-red-500/5 ${compact ? "p-4" : "p-5"}`}>
          <p className="text-sm font-semibold text-red-300">{error}</p>
          <button type="button" onClick={load} className="mt-3 text-sm font-bold text-[#CCFF00]" data-testid="reviews-retry">{translateLabel("Try again")}</button>
        </div>
      ) : (
        <>
          {data.summary?.count > 0 && (
            <div className={`mb-4 rounded-2xl border border-[#CCFF00]/20 bg-[#CCFF00]/5 ${compact ? "p-4" : "p-5"}`} data-testid="community-rating-summary">
              <div className="label-tiny text-[#CCFF00]">{translateLabel("BPF community rating")}</div>
              <div className="mt-2 flex items-center gap-3">
                <Stars value={Math.round(data.summary?.average ?? 0)} />
                <span className="font-display text-xl font-bold text-white">{communityRatingText(data.summary)}</span>
              </div>
            </div>
          )}

          {editing && (
            <form onSubmit={submit} className="mb-5 rounded-2xl border border-white/10 bg-[#121214] p-5" data-testid="review-editor">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl font-bold uppercase text-white">{ownReview ? "Edit your review" : "Your review"}</h3>
                  <p className="mt-1 text-xs text-zinc-500">{translateLabel("Rating is required. Text and tags are optional.")}</p>
                </div>
                <button type="button" onClick={closeEditor} className="text-zinc-500 hover:text-white" aria-label={translateLabel("Close review editor")}><XIcon size={20} /></button>
              </div>

              <div className="mt-5">
                <div className="label-tiny mb-2">{translateLabel("Rating")}</div>
                <Stars value={form.rating} interactive onChange={(rating) => setForm((current) => ({ ...current, rating }))} />
              </div>

              <div className="mt-5">
                <div className="label-tiny mb-2">{translateLabel("Tags")}</div>
                <div className="flex flex-wrap gap-2">
                  {DESTINATION_REVIEW_TAGS.map(({ value, label }) => {
                    const active = form.tags.includes(value);
                    return (
                      <button
                        type="button"
                        key={value}
                        onClick={() => setForm((current) => ({ ...current, tags: toggleReviewTag(current.tags, value) }))}
                        data-testid={`review-tag-${value.toLowerCase()}`}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${active ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="mt-5 block">
                <span className="label-tiny">{translateLabel("Review")}</span>
                <textarea
                  value={form.text}
                  onChange={(event) => setForm((current) => ({ ...current, text: event.target.value }))}
                  maxLength={2000}
                  rows={5}
                  data-testid="review-text"
                  placeholder={translateLabel("What should another rider know?")}
                  className="mt-2 w-full resize-y rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none"
                />
              </label>

              <div className="mt-5 flex flex-wrap gap-2">
                <button type="submit" disabled={saving} data-testid="save-review-btn" className="rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-50">
                  {saving ? "Saving…" : "Save review"}
                </button>
                {ownReview && (
                  <button type="button" disabled={saving} onClick={remove} data-testid="remove-review-btn" className="rounded-full border border-white/10 px-5 py-2.5 text-sm font-bold text-zinc-300 hover:bg-white/5 disabled:opacity-50">
                    Remove review
                  </button>
                )}
              </div>
            </form>
          )}

          {reviews.length === 0 ? (
            <div className={compact ? "flex items-center gap-3 rounded-2xl border border-white/10 bg-[#121214] p-4 text-left" : "rounded-2xl border border-white/10 bg-[#121214] p-5 text-center"}>
              <StarIcon size={compact ? 24 : 28} className={compact ? "shrink-0 text-[#CCFF00]" : "mx-auto text-[#CCFF00]"} />
              <div>
                <p className={`${compact ? "text-base" : "mt-3 text-xl"} font-display font-bold text-white`}>{translateLabel("No community reviews yet")}</p>
                <p className={`mt-1 text-zinc-500 ${compact ? "text-xs" : "text-sm"}`}>{translateLabel("Be the first rider to share a rating.")}</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="community-review-list">
              {reviews.map((review) => (
                <article key={review.id} className="rounded-2xl border border-white/10 bg-[#121214] p-5" data-testid="community-review-card">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold text-white" data-testid="review-author">{review.author?.name || "Rider"}</div>
                      <Stars value={review.rating} />
                    </div>
                    <span className="text-xs text-zinc-600">{formatDate(review.updated_at || review.created_at)}</span>
                  </div>
                  {review.tags?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {review.tags.map((tag) => <span key={tag} className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-semibold text-zinc-300">{REVIEW_TAG_LABELS[tag] || tag}</span>)}
                    </div>
                  )}
                  {review.text && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">{review.text}</p>}
                  <div className="mt-4 flex flex-wrap items-start gap-3 border-t border-white/5 pt-3">
                    <button
                      type="button"
                      onClick={() => handleHelpful(review)}
                      disabled={helpfulSavingId === review.id}
                      data-testid="review-helpful-btn"
                      aria-pressed={Boolean(review.viewer_has_helpful)}
                      className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${review.viewer_has_helpful ? "bg-[#CCFF00] text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}
                    >
                      Helpful{review.helpful_count > 0 ? ` · ${review.helpful_count}` : ""}
                    </button>
                    <ReportContent targetType="REVIEW" targetId={review.id} compact />
                  </div>
                </article>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
